import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { tenantApi } from '../../api/endpoints/tenant';
import { useAuth } from '../../hooks/useAuth';
import { RoleFormCore } from '../roles/RoleFormCore';
import type { Permission } from '../../api/types/permissions';

interface RoleFormProps {
    /** Se llama tras crear OK (ej. cerrar el modal). Si no se pasa, navega a la lista. */
    onDone?: () => void;
    /** Pre-pobla el nombre del rol (ej. "Clonar a custom" → "Copia de X"). Sin él: vacío. */
    initialName?: string;
    /** Pre-pobla los permisos seleccionados (ej. clonar desde un rol de sistema). Sin él: []. */
    initialPermissions?: Permission[];
}

/**
 * Alta de un rol custom del PROPIO tenant (`/tenant/roles`). Wrapper delgado sobre `RoleFormCore`:
 * solo alta (sin edición), catálogo de permisos global (picker por defecto), destino = el tenant del
 * usuario logueado. La lógica compartida (schema, slug preview, colisión de slug) vive en el core.
 */
export function RoleForm({ onDone, initialName, initialPermissions }: RoleFormProps = {}) {
    const { user } = useAuth();
    const tid = user?.tenantId ?? '';
    const navigate = useNavigate();
    const qc = useQueryClient();

    return (
        <RoleFormCore
            strategy={{
                supportsEdit: false,
                initial: {
                    displayName: initialName ?? '',
                    description: '',
                    permissions: initialPermissions ?? [],
                },
                create: (body, opts) => tenantApi.createRole(tid, body, opts),
                invalidate: () => qc.invalidateQueries({ queryKey: ['tenant', tid, 'roles'] }),
                onDone,
                onCreatedFallback: () => navigate({ to: '/tenant/roles' }),
                idPrefix: 'rf',
                nameHint: 'Cómo quieres que se vea. Ej: Supervisor de Cobranza',
                permissionsLabel: 'Permisos',
                createSubmitLabel: 'Crear rol',
            }}
        />
    );
}
